import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { RecurringInvoiceStatus } from '@features/recurringInvoices/enums/recurringInvoiceStatus'
import { useRecurringInvoiceStatus } from '@features/recurringInvoices/hooks/useRecurringInvoiceStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useDirtyRoute } from '../../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { useSegment } from '../../../../../../hooks'
import { getOrganizationViewUrl } from '../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { useSaveRecurringInvoiceDraft } from './useSaveRecurringInvoiceDraft'

export const useSaveAndActivateRecurringInvoice = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { track } = useSegment()

  const { setDirty } = useDirtyRoute()
  const { organization } = useUserOrganization()
  const { saveRecurringInvoiceDraft, isLoading, isSaving } = useSaveRecurringInvoiceDraft()
  const { activateRecurringInvoice, isProcessing: isActivating } = useRecurringInvoiceStatus()

  const saveAndActivateRecurringInvoice = useCallback(() => {
    saveRecurringInvoiceDraft({
      onSuccess: (recurringInvoice) => {
        const isRecurringInvoiceDraft = recurringInvoice?.status?.status === RecurringInvoiceStatus.Draft

        if (isRecurringInvoiceDraft && recurringInvoice?.id) {
          const recurringInvoiceId = String(recurringInvoice.id)

          activateRecurringInvoice({
            recurringInvoiceId,
            onSuccess: () => {
              setDirty(false)

              track('Recurring Invoice Started (Client)')
              track('XXX - Invoicing Core - Recurring invoice saved & activated')

              notify({
                id: NotificationKeys.RecurringInvoiceSavedAndActivated,
                message: t('recurring_invoices.save_and_activate.notification.success'),
                variant: 'success',
              })

              navigate(
                `${getOrganizationViewUrl(
                  organization?.url,
                  reactRoute.organization.recurringInvoices.getMainRoute(),
                )}`,
              )
            },
          })
        }
      },
    })
  }, [activateRecurringInvoice, navigate, organization?.url, saveRecurringInvoiceDraft, setDirty, t, track])

  return {
    isLoading,
    isProcessing: isSaving || isActivating,
    saveAndActivateRecurringInvoice,
  }
}
